import { memo } from 'react';
import { Pressable, View } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, withOpacity } from '@/theme/colors';

export type NotificationButtonProps = {
  onPress?: () => void;
  hasUnreadNotification?: boolean;
};

const NOTIFICATION_SIZE = 40;

/**
 * Liquid-glass notification bell with an unread dot. Shared so every header
 * (Home, Kids, …) renders an identical notification affordance.
 */
function NotificationButton({ onPress, hasUnreadNotification = true }: NotificationButtonProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const notificationSize = scale(NOTIFICATION_SIZE);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="Notifications"
      onPress={onPress}
      style={({ pressed }) => [
        styles.notificationButton,
        pressed ? styles.notificationButtonPressed : null,
      ]}>
      <Stack
        viewType="liquid-glass"
        width={notificationSize}
        height={notificationSize}
        borderRadius={notificationSize / 2}
        align="center"
        justify="center"
        backgroundColor={withOpacity(colors['0'], 0.5)}
        overlayColor={withOpacity(colors['0'], 0.5)}
        intensity={20}
        tint="light">
        <SvgIcon name="IcNotification1" color={colors['10']} />
      </Stack>
      {hasUnreadNotification ? <View style={styles.notificationBadge} /> : null}
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  notificationButton: {
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: scale(16),
  },
  notificationButtonPressed: {
    opacity: 0.72,
  },
  notificationBadge: {
    position: 'absolute',
    top: scale(8),
    right: scale(8),
    width: scale(10),
    height: scale(10),
    borderRadius: scale(99999),
    borderWidth: 1,
    borderColor: colors['0'],
    backgroundColor: colors.Red,
  },
}));

export default memo(NotificationButton);
